import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Camera, Upload, IdCard, FileText, Shield, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { aiApi } from '../lib/api';
import { fileToBase64 } from '../lib/voice';

const FIELDS = [
  { k: 'name', l: 'Name (Gegner)' },
  { k: 'address', l: 'Adresse' },
  { k: 'phone', l: 'Telefon' },
  { k: 'license_plate', l: 'Kennzeichen' },
  { k: 'vehicle_make', l: 'Marke' },
  { k: 'vehicle_model', l: 'Modell' },
  { k: 'vin', l: 'FIN/VIN' },
  { k: 'drivers_license_number', l: 'Führerscheinnummer' },
  { k: 'insurance_company', l: 'Versicherung' },
  { k: 'insurance_policy_number', l: 'Polizzennummer' },
];

const DOCS = [
  { key: 'fuehrerschein', label: 'Führerschein', icon: IdCard, hint: 'Vor- und Nachname, Führerscheinnummer' },
  { key: 'zulassungsschein', label: 'Zulassungsschein', icon: FileText, hint: 'Halter, Kennzeichen, Fahrzeug, FIN' },
  { key: 'versicherungskarte', label: 'Versicherungskarte / Grüne Karte', icon: Shield, hint: 'Versicherung, Polizzennummer' },
];

export default function OpponentDataPanel({ value, onChange, testid = 'opponent-panel' }) {
  const [data, setData] = useState(value || {});
  const [busy, setBusy] = useState(null);
  const refs = React.useRef({});

  React.useEffect(() => { setData(value || {}); }, [value]);

  const update = (next) => {
    setData(next);
    onChange?.(next);
  };
  const setField = (k, v) => update({ ...data, [k]: v });

  const onScan = async (docKey, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error('Datei zu groß');
    if (!/^image\//.test(file.type)) return toast.error('Bitte ein Foto hochladen');
    setBusy(docKey);
    try {
      const { base64 } = await fileToBase64(file);
      const { data: res } = await aiApi.ocrOpponent(base64, docKey);
      const r = res.result || {};
      const next = { ...data };
      for (const k of Object.keys(r)) {
        if (r[k] && !next[k]) next[k] = r[k];
      }
      update(next);
      if (r.confidence > 0) toast.success(`${DOCS.find(d => d.key === docKey).label} erkannt`);
      else toast.error('Daten nicht erkannt');
    } catch (e) {
      toast.error('Erkennung fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid={testid} className="space-y-4">
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div>
            <div className="font-heading font-semibold text-slate-900">KI-Erkennung Unfallgegner</div>
            <div className="text-xs text-slate-600">Foto vom Führerschein, Zulassungsschein oder Versicherungskarte machen — die Felder werden automatisch ausgefüllt.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {DOCS.map((d) => (
              <div key={d.key} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center gap-2">
                  <d.icon className="h-4 w-4 text-[hsl(var(--primary))]" />
                  <div className="font-medium text-sm">{d.label}</div>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{d.hint}</div>
                <Button type="button" size="sm" disabled={busy === d.key} onClick={() => refs.current[d.key]?.click()} className="mt-2 bg-[hsl(var(--accent))] hover:bg-[hsl(217_90%_48%)] text-white w-full" data-testid={`opp-scan-${d.key}`}>
                  {busy === d.key ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Camera className="h-3.5 w-3.5 mr-1.5" />}
                  {busy === d.key ? 'Lese...' : 'Foto machen'}
                </Button>
                <input ref={(el) => (refs.current[d.key] = el)} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files && onScan(d.key, e.target.files[0])} data-testid={`opp-input-${d.key}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4">
          <div className="font-heading font-semibold text-slate-900 mb-2">Daten des Unfallgegners</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <div key={f.k}>
                <Label className="text-xs">{f.l}</Label>
                <Input value={data[f.k] || ''} onChange={(e) => setField(f.k, e.target.value)} className="h-11 rounded-xl mt-1" data-testid={`opp-field-${f.k}`} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
